/**
 * PortalHeader — the bar above every page, inside the content column.
 *
 * Left: the page's bearing (section / page, from the nav tree, not the URL).
 * Middle: the ⌘K page search. Right: environment, quick actions, appearance,
 * alerts and the profile menu.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftRight, Bell, ChevronDown, LineChart, LogOut, Menu, Monitor, Moon, Search, Sun, TrendingUp, Users, Wallet,
} from "lucide-react";
import { useLogout, useMe } from "@/hooks/useAuth";
import { useTraderDashboard } from "@/hooks/useTrader";
import { useAdminNotifications } from "@/hooks/useMisc";
import { useTradingServerInfo } from "@/hooks/useTrading";
import { useCurrentTenant } from "@/hooks/useTenant";
import { resolveEnabledModules } from "@/lib/modules";
import { markNotificationsRead, useReadNotifications } from "@/hooks/useNotificationReads";
import { useTheme, type ThemeMode } from "@/hooks/useTheme";
import { Brand } from "./Brand";
import { cn } from "@/lib/utils";

const PRIORITY_DOT: Record<string, string> = {
  critical: "bg-danger", high: "bg-warning", normal: "bg-accent", low: "bg-fg-subtle",
};

/** Shared geometry for the bar's icon buttons — 36px, 6px radius. */
const ICON_BTN =
  "grid h-10 w-10 place-items-center rounded-control text-fg-muted transition-colors duration-feedback ease-brand hover:bg-bg-elevated hover:text-fg";

function NotificationBell() {
  const { data } = useAdminNotifications();
  const read = useReadNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const rows = data?.rows ?? [];
  const count = rows.filter((n) => !read.has(n.id)).length;

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button aria-label="Notifications" onClick={() => setOpen((v) => !v)} className={cn(ICON_BTN, "relative")}>
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.5} />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-semibold text-primary-foreground">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[340px] max-w-[calc(100vw-24px)] animate-fade-in overflow-hidden rounded-xl border border-border bg-card shadow-overlay">
          <div className="flex items-center justify-between border-b border-border px-3 py-2 text-[13px] font-medium">
            Notifications <span className="text-[11px] font-normal text-fg-subtle">{count} pending</span>
          </div>
          <div className="max-h-96 divide-y divide-border overflow-y-auto">
            {rows.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-fg-subtle">Nothing needs your attention.</p>
            ) : (
              rows.slice(0, 15).map((n) => (
                <button
                  key={n.id}
                  onClick={() => { markNotificationsRead([n.id]); setOpen(false); nav(n.link); }}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors duration-feedback ease-brand hover:bg-bg-elevated"
                >
                  <span className={cn("mt-1 h-2 w-2 shrink-0 rounded-full", PRIORITY_DOT[n.priority] ?? "bg-primary")} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium">{n.title}</span>
                    <span className="block truncate text-[11px] text-fg-subtle">{n.body}</span>
                  </span>
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => { setOpen(false); nav("/admin/notifications"); }}
            className="w-full border-t border-border px-3 py-2 text-center text-xs text-primary underline-offset-4 hover:underline"
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Separate demo environments unmistakably from live.
 * While no platform is connected the CRM serves simulated trading data, and
 * saying so in the bar beats letting an operator assume the figures are live.
 */
function EnvironmentChip() {
  const { data } = useTradingServerInfo();
  if (!data) return null;
  const sim = data.mode === "sim";
  return (
    <span
      className={cn(
        "hidden h-8 items-center gap-1.5 rounded-full border px-3 text-[11px] font-semibold xl:inline-flex",
        sim ? "border-warning/30 bg-warning-soft text-warning" : "border-success/30 bg-success-soft text-success",
      )}
      title={sim ? "No trading platform is connected — figures come from the built-in simulator." : `Live platform · ${data.serverName}`}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", sim ? "bg-warning" : "bg-success")} aria-hidden />
      {sim ? "Simulated data" : "Live platform"}
    </span>
  );
}

function ProfileMenu() {
  const { data: me } = useMe();
  const logout = useLogout();
  const nav = useNavigate();
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = (me?.fullName ?? "W")
    .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

  const modes: Array<{ v: ThemeMode; l: string; I: typeof Sun }> = [
    { v: "light", l: "Light", I: Sun },
    { v: "dark", l: "Dark", I: Moon },
    { v: "system", l: "Auto", I: Monitor },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 rounded-full border border-border bg-card py-1 pl-1 pr-3 transition-colors duration-feedback ease-brand hover:border-border-control"
      >
        <span className="relative grid h-8 w-8 place-items-center rounded-full bg-grad-brand font-display text-[11px] font-bold text-[#06121F]">
          {initials}
        </span>
        <span className="hidden max-w-[140px] flex-col items-start leading-tight md:flex">
          <span className="truncate text-[12.5px] font-semibold text-fg">{me?.fullName ?? "Signed in"}</span>
          <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-fg-subtle">{me?.role ?? "—"}</span>
        </span>
        <ChevronDown className="hidden h-3.5 w-3.5 text-fg-subtle md:block" strokeWidth={2} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-50 mt-2 w-64 animate-fade-in overflow-hidden rounded-xl border border-border bg-card shadow-overlay">
          <div className="border-b border-border px-4 py-3">
            <p className="truncate text-[13px] font-semibold">{me?.fullName ?? "Signed in"}</p>
            <p className="truncate text-xs text-fg-subtle">{me?.email}</p>
          </div>
          <div className="px-4 py-3">
            <p className="eyebrow mb-2">Appearance</p>
            <div className="grid grid-cols-3 gap-1 rounded-control bg-bg-elevated p-1">
              {modes.map(({ v, l, I }) => (
                <button
                  key={v}
                  onClick={() => setMode(v)}
                  aria-pressed={mode === v}
                  className={cn(
                    "flex h-8 items-center justify-center gap-1.5 rounded-lg text-xs font-medium transition-colors duration-feedback ease-brand",
                    mode === v ? "bg-card text-fg shadow-soft" : "text-fg-muted hover:text-fg",
                  )}
                >
                  <I className="h-3.5 w-3.5" strokeWidth={1.8} /> {l}
                </button>
              ))}
            </div>
          </div>
          <button
            role="menuitem"
            onClick={async () => { await logout.mutateAsync(); nav("/login"); }}
            className="flex w-full items-center gap-2.5 border-t border-border px-4 py-3 text-left text-[13px] text-fg-muted transition-colors duration-feedback ease-brand hover:bg-bg-elevated hover:text-danger"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.6} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

interface HeaderProps {
  onMenuToggle?: () => void;
  onSearch?: () => void;
  /** Section and page of the current route, from the nav tree. */
  section?: string | null;
  page?: string | null;
  parent?: string | null;
}

export function PortalHeader({ onMenuToggle, onSearch, section, page, parent }: HeaderProps) {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const { resolved, toggle } = useTheme();

  const { data: tenantInfo } = useCurrentTenant();
  const enabledModules = resolveEnabledModules(tenantInfo?.tenant?.settings?.modules);
  const copyTradingOn = enabledModules.has("copy-trading");

  const managerHasAdmin = me?.role === "manager" && (me.fullAccess || (me.permissions?.length ?? 0) > 0);
  const inAdmin = pathname.startsWith("/admin");
  const isTrader = me?.role === "trader" || me?.role === "ib" || me?.role === "affiliate";
  const isStaff = me?.role === "admin" || managerHasAdmin;
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-bg/85 supports-[backdrop-filter]:bg-bg/70">
      <div className="flex h-16 items-center gap-3 px-4 backdrop-blur-md sm:px-6 xl:px-10">
        {onMenuToggle && (
          <button onClick={onMenuToggle} aria-label="Open menu" className={cn(ICON_BTN, "shrink-0 lg:hidden")}>
            <Menu className="h-5 w-5" strokeWidth={1.6} />
          </button>
        )}
        <Brand variant="symbol" className="h-8 w-8 shrink-0 lg:hidden" />

        {/* Bearing: where you are, section › page */}
        <div className="min-w-0 flex-1">
          {section && (
            <p className="hidden truncate text-[11px] font-medium text-fg-subtle sm:block">
              {section}{parent ? <> <span className="text-border-control">/</span> {parent}</> : null}
            </p>
          )}
          <p className="truncate font-display text-[15px] font-bold leading-tight text-fg sm:text-base">{page ?? "Wachsen Markets"}</p>
        </div>

        {onSearch && (
          <button
            onClick={onSearch}
            className={cn("hidden h-10 items-center gap-2.5 rounded-control border border-border bg-card px-3 text-left text-[13px] text-fg-subtle transition-colors duration-feedback ease-brand hover:border-border-control md:flex", isTrader ? "w-[200px]" : "w-[240px] xl:w-[300px]")}
          >
            <Search className="h-4 w-4" strokeWidth={1.8} />
            <span className="flex-1 truncate whitespace-nowrap">{isTrader ? "Search…" : "Jump to a page…"}</span>
            <kbd className="rounded-md border border-border bg-bg-elevated px-1.5 py-0.5 font-sans text-[10.5px] font-semibold text-fg-muted">{isMac ? "⌘" : "Ctrl"} K</kbd>
          </button>
        )}
        {onSearch && (
          <button onClick={onSearch} aria-label="Search pages" className={cn(ICON_BTN, "md:hidden")}>
            <Search className="h-[18px] w-[18px]" strokeWidth={1.6} />
          </button>
        )}

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {isStaff && <EnvironmentChip />}

          {managerHasAdmin && (
            <Link
              to={inAdmin ? "/manager/dashboard" : "/admin/dashboard"}
              className="hidden h-10 items-center gap-1.5 rounded-control border border-border px-3 text-xs font-semibold text-fg-muted transition-colors duration-feedback ease-brand hover:border-border-control hover:text-fg sm:inline-flex"
              title={inAdmin ? "Switch to your relationship-manager portal" : "Switch to the back office"}
            >
              <ArrowLeftRight className="h-4 w-4" strokeWidth={1.6} /> {inAdmin ? "RM area" : "Back office"}
            </Link>
          )}

          {isTrader && (
            <nav className="hidden items-center gap-1.5 xl:flex" aria-label="Quick actions">
              <WalletPill />
              <Link to="/trader/web-trader" className={QUICK} title="Web trader">
                <LineChart className="h-4 w-4" strokeWidth={1.6} /> <span className="hidden 2xl:inline">Web trader</span>
              </Link>
              {copyTradingOn && (
                <Link to="/trader/copy-trading" className={QUICK} title="Social trading">
                  <Users className="h-4 w-4" strokeWidth={1.6} /> <span className="hidden 2xl:inline">Social</span>
                </Link>
              )}
            </nav>
          )}
          {isTrader && (
            <Link
              to="/trader/deposit"
              className="hidden h-10 items-center gap-1.5 rounded-control bg-primary px-4 text-[13px] font-semibold text-primary-foreground shadow-glow transition-transform duration-feedback ease-brand hover:-translate-y-px sm:inline-flex"
            >
              <TrendingUp className="h-4 w-4" strokeWidth={2} /> Deposit
            </Link>
          )}

          <button
            onClick={toggle}
            aria-label={resolved === "dark" ? "Switch to light appearance" : "Switch to dark appearance"}
            className={cn(ICON_BTN, "hidden sm:grid")}
          >
            {resolved === "dark"
              ? <Sun className="h-[18px] w-[18px]" strokeWidth={1.6} />
              : <Moon className="h-[18px] w-[18px]" strokeWidth={1.6} />}
          </button>

          {isStaff && <NotificationBell />}

          <ProfileMenu />
        </div>
      </div>
    </header>
  );
}

const QUICK =
  "inline-flex h-10 items-center gap-1.5 rounded-control px-3 text-xs font-semibold text-fg-muted transition-colors duration-feedback ease-brand hover:bg-bg-elevated hover:text-fg";

/** Main-wallet balance for the trader / partner bar. */
function WalletPill() {
  const dash = useTraderDashboard();
  const balance = dash.data?.wallet ? Number(dash.data.wallet.balance) : null;
  return (
    <Link
      to="/trader/dashboard"
      title="Main wallet balance"
      className="inline-flex h-10 items-center gap-2 rounded-control border border-border bg-card px-3 text-xs font-semibold text-fg transition-colors duration-feedback ease-brand hover:border-border-control"
    >
      <Wallet className="h-4 w-4 text-accent" strokeWidth={1.6} />
      <span className="tnum">{balance == null ? "—" : `USD ${balance.toFixed(2)}`}</span>
    </Link>
  );
}
